
import './App.css';
import React, { useEffect, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { API_URL, PRIVACY_POLICY_URL } from './config';

// Layout encoded as rows: favorite is 2-3, improvements is 2-1-2 (matches the Figma design)
const FAVORITE_ROWS = [
  ['Sound Clarity', 'Noise Reduction'],
  ['Comfort', 'Easy to Use', 'Other'],
];
const IMPROVEMENT_ROWS = [
  ['Better Battery Life', 'Easier To Use'],
  ['Improved Sound Quality'],
  ['More Comfortable Fit', 'Other'],
];
const FAVORITE_OPTIONS = FAVORITE_ROWS.flat();
const IMPROVEMENT_OPTIONS = IMPROVEMENT_ROWS.flat();

const CONSENT_YES = 1;
const CONSENT_NO = 0;

function ChatUI({ onRequestComplete }) {
  const [isDisabled, setIsDisabled] = useState(false);

  const getUrlParam = (key) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(key);
  };

  const authorization = getUrlParam('authorization') || '';
  const productType = parseInt(getUrlParam('productType') || '0', 10);
  const urlEmail = getUrlParam('email') || '';
  const urlPhone = getUrlParam('phone') || '';

  const [rating, setRating] = useState(0); // 1..5
  const [npsRating, setNpsRating] = useState(0); // 1..5 (stored as string per backend contract)
  const [favoriteSet, setFavoriteSet] = useState(new Set());
  const [improvementSet, setImprovementSet] = useState(new Set());

  const [followUpConsent, setFollowUpConsent] = useState(null);
  const [followUpContact, setFollowUpContact] = useState('');

  const [profile, setProfile] = useState({ email: '', phone: '' });
  const contactRef = useRef(null);

  // Pre-fill on mount: GET /user/getUserById + GET /info/last in parallel.
  useEffect(() => {
    if (!authorization) {
      return;
    }
    const headers = { Authorization: authorization };
    const profilePromise = fetch(`${API_URL}/user/getUserById`, { headers })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    const lastQuery = productType ? `?productType=${productType}` : '';
    const lastPromise = fetch(`${API_URL}/info/last${lastQuery}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    Promise.all([profilePromise, lastPromise]).then(([prof, last]) => {
      if (prof && prof.code === 200 && prof.data) {
        setProfile({ email: prof.data.email || '', phone: prof.data.phone || '' });
      }
      if (last && last.code === 200 && last.data) {
        const d = last.data;
        if (d.rating) setRating(parseInt(d.rating, 10) || 0);
        if (d.nps) setNpsRating(parseInt(d.nps, 10) || 0);
        if (d.favoriteFt) {
          setFavoriteSet(new Set(d.favoriteFt.split(',').map(s => s.trim()).filter(Boolean)));
        }
        if (d.improvements) {
          setImprovementSet(new Set(d.improvements.split(',').map(s => s.trim()).filter(Boolean)));
        }
        if (d.followUpConsent === CONSENT_YES || d.followUpConsent === CONSENT_NO) {
          setFollowUpConsent(d.followUpConsent);
        }
        if (d.followUpContact) {
          setFollowUpContact(d.followUpContact);
        }
      }
    });
  }, [API_URL, authorization, productType]);

  const toggleFromSet = (setter, current, value) => {
    const next = new Set(current);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };

  const handleSelectYes = () => {
    setFollowUpConsent(CONSENT_YES);
    if (!followUpContact) {
      const fallback = profile.email || profile.phone || urlEmail || urlPhone || '';
      if (fallback) setFollowUpContact(fallback);
    }
  };

  const handleSelectNo = () => {
    setFollowUpConsent(CONSENT_NO);
  };

  function callbackEmail(json) {
    try {
      const obj = window.JSON.parse(json);
      if (obj.code === 200) {
        onRequestComplete();
      } else {
        toast.error('Send feedback failed: ' + obj.message, { position: 'top-center' });
        setIsDisabled(false);
      }
    } catch (error) {
      toast.error('Error during send feedback: ' + error, { position: 'top-center' });
      setIsDisabled(false);
    }
  }

  const sendUser = (callback) => {
    const httpRequest = new XMLHttpRequest();
    httpRequest.open('POST', `${API_URL}/info/add/v2`, true);
    httpRequest.setRequestHeader('Content-Type', 'application/json;charset=UTF-8');
    if (authorization) {
      httpRequest.setRequestHeader('Authorization', authorization);
    }
    const data = JSON.stringify({
      productType,
      rating: rating ? String(rating) : '',
      favoriteFt: Array.from(favoriteSet).join(','),
      improvements: Array.from(improvementSet).join(','),
      nps: npsRating ? String(npsRating) : '',
      followUpConsent: followUpConsent === CONSENT_YES ? 1 : 0,
      followUpContact: followUpConsent === CONSENT_YES ? followUpContact.trim() : null,
    });
    httpRequest.send(data);
    httpRequest.onreadystatechange = function () {
      if (httpRequest.readyState === 4 && httpRequest.status === 200) {
        callback(httpRequest.responseText);
      }
    };
  };

  // Submit 启用条件：rating、两个 chip 问题各至少一选、followUp 选了。contact 始终可选。
  const isFormValid =
    rating > 0
    && favoriteSet.size > 0
    && improvementSet.size > 0
    && followUpConsent !== null;

  const handleSubmit = () => {
    if (!isFormValid) {
      return;
    }
    setIsDisabled(true);
    sendUser(callbackEmail);
  };

  const renderStars = (value, onChange) => (
    <div className="pc-stars">
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} className="pc-star-item" onClick={() => onChange(n)}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill={n <= value ? '#F05627' : 'none'}
            stroke="#F05627"
            strokeWidth="1"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </span>
      ))}
    </div>
  );

  const renderChipRows = (rows, selectedSet, setter) => (
    <div className="pc-chip-grid">
      {rows.map((row, idx) => (
        <div className="pc-chip-row" key={idx}>
          {row.map(opt => (
            <button
              key={opt}
              type="button"
              className={`pc-chip ${selectedSet.has(opt) ? 'pc-chip--active' : ''}`}
              onClick={() => toggleFromSet(setter, selectedSet, opt)}
            >
              {opt}
            </button>
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="container">
      <div className="container-top-left">
        <div className="container-top-left-white">
          <div className="div-grey-container-center div-margin-top-60 div-mid-text">
            Write a Product Review
          </div>

          <div className="div-left-text-little-title div-margin-top-60">
            <span style={{ fontFamily: 'Lato', fontWeight: '700', fontSize: '18px' }}>OVER 1.5 BILLION</span>{' '}
            people worldwide experience hearing loss, yet 83% go without help. At ELEHEAR, we believe quality hearing support should be accessible to everyone. Your survey feedback helps us make this vision a reality. Thank you for being part of this meaningful change.
          </div>

          <div className="div-left-text div-margin-top-60">How would you rate your hearing aids?</div>
          {renderStars(rating, setRating)}

          <div className="div-left-text div-margin-top-60">What's your favorite feature of the product?</div>
          {renderChipRows(FAVORITE_ROWS, favoriteSet, setFavoriteSet)}

          <div className="div-left-text div-margin-top-60">What improvements do you think the product still needs?</div>
          {renderChipRows(IMPROVEMENT_ROWS, improvementSet, setImprovementSet)}

          <div className="div-left-text div-margin-top-60">How likely are you to recommend our product to friends and family?</div>
          {renderStars(npsRating, setNpsRating)}

          <div className="div-left-text div-margin-top-60">Would you like us to follow up with you?</div>

          <div
            className={`pc-radio-row ${followUpConsent === CONSENT_YES ? 'pc-radio-row--active' : ''}`}
            onClick={handleSelectYes}
          >
            <span className={`pc-radio-dot ${followUpConsent === CONSENT_YES ? 'pc-radio-dot--checked' : ''}`} />
            <span className="pc-radio-text">Yes, I'm happy to be contacted</span>
          </div>

          {followUpConsent === CONSENT_YES && (
            <div className="pc-contact-block">
              <div className="pc-contact-hint">Please leave your phone number or email address:</div>
              <input
                ref={contactRef}
                className="pc-contact-input"
                placeholder=""
                value={followUpContact}
                onChange={(e) => setFollowUpContact(e.target.value)}
              />
            </div>
          )}

          <div
            className={`pc-radio-row ${followUpConsent === CONSENT_NO ? 'pc-radio-row--active' : ''}`}
            onClick={handleSelectNo}
          >
            <span className={`pc-radio-dot ${followUpConsent === CONSENT_NO ? 'pc-radio-dot--checked' : ''}`} />
            <span className="pc-radio-text">No, I prefer not to be contacted</span>
          </div>

          <div className="sub-text div-margin-top-60">
            By submitting, you agree to our
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'blue', textDecoration: 'none' }}
            >
              <span style={{ color: 'rgba(240, 86, 39, 1)' }}> Privacy Policy</span>
            </a>
            . We will strictly protect your personal information.
          </div>

          <button
            disabled={isDisabled || !isFormValid}
            className={`share-button-link ${(!isFormValid || isDisabled) ? 'share-button-link--disabled' : ''}`}
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>

        <div className="footer-placeholder"></div>
      </div>

      <Toaster />
    </div>
  );
}

export default ChatUI;
